import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router';
import { ActivityIndicator, Box, Button, Text } from '../../ui';
import { useQuoteExpiration } from '../../hooks';
import { Page, NavBar, NavRow, NavBackButton, NavGroup, AppBarSpacer, ErrorBoundary, LayoutBox } from '../../components';
import moment from 'moment-timezone';
import { ProvideQuoteData, useQuoteData } from './context/QuoteDataContext';
import { ProvideQuotePageContext } from './context/QuotePageContext';
import { QuoteView } from './content/QuoteView';
import { QuoteActions } from './content/QuoteView/QuoteActions';
import { useAdjustmentState } from './context/QuoteAdjustment/AdjustmentContext';
import { Prompt } from 'react-router-dom';
import { QuoteNav } from './content/QuoteView/QuoteNav';
import { QuoteTabs } from './content/QuoteView/QuoteTabs';

export function QuotePage() {
  const printRef = useRef();

  const { id } = useParams();

  const renderLoading = useCallback(() => <ActivityIndicator />, []);
  const renderEmpty = useCallback(() => <Text>Error getting quote</Text>, []);

  return (
    <Page padTop={({ theme }) => theme.breakpoints({ xs: 0, sm: theme.sizes.appBarHeight })}>
      <ErrorBoundary>
        <ProvideQuoteData id={id} renderLoading={renderLoading} renderEmpty={renderEmpty}>
          <ProvideQuotePageContext>
            <QuotePageContent printRef={printRef} />
          </ProvideQuotePageContext>
        </ProvideQuoteData>
      </ErrorBoundary>
    </Page>
  );
}

const QuotePageContent = memo(function QuotePageContent({ printRef }) {
  const { data, productCoverages, macavity, update, updating, lastUpdated, updateMacavity } = useQuoteData();
  const { setCurrentTab = () => {}, currentTab } = useAdjustmentState();

  const quoteTabs = {
    FEES: 'FEES',
    COVERAGES: 'COVERAGES',
    CAT_MODELING: 'CAT_MODELING',
    RISK_CONCENTRATION: 'RISK_CONCENTRATION',
    AUTHORITY: 'AUTHORITY',
    LOSS_MANAGEMENT: 'LOSS_MANAGEMENT',
    BIND_FORMS: 'BIND_FORMS',
    // Every status, not only bound: the stamping wording and state documents matter before binding too.
    BIND_INSTRUCTIONS: 'BIND_INSTRUCTIONS',
    ATTACHMENTS: 'ATTACHMENTS',
    UNDERWRITER_NOTES: 'UNDERWRITER_NOTES',
  };

  return (
    <>
      <Nav data={data} update={update} updating={updating} lastUpdated={lastUpdated} printRef={printRef} />
      <Box>
        <QuoteView
          data={data}
          update={update}
          updating={updating}
          lastUpdated={lastUpdated}
          productCoverages={productCoverages}
          macavity={macavity}
          printRef={printRef}
        />
        <QuoteTabs
          data={data}
          applicationTabsKeys={quoteTabs}
          productCoverages={productCoverages}
          macavity={macavity}
          update={update}
          updating={updating}
          lastUpdated={lastUpdated}
          quotesView={true}
          updateMacavity={updateMacavity}
          setCurrentTab={setCurrentTab}
          currentTab={currentTab}
        />
        {/* <BindFormsView data={data} update={update} updating={updating} lastUpdated={lastUpdated} /> */}
      </Box>
      <RenderExitPromptIfEditing />
    </>
  );
});

const Nav = memo(function Nav({ data, updating, update, lastUpdated, printRef }) {
  const historyDisplayText = useMemo(() => {
    if (data) {
      // const lastTimestamp = moment().subtract(339, 'days');
      const lastTimestamp = data.lastChangeAt ? moment(data.lastChangeAt) : data.createdAt ? moment(data.createdAt) : null;
      if (lastTimestamp) {
        const now = moment();
        const dayDiff = now.diff(lastTimestamp, 'days');
        if (dayDiff < 2) {
          const hourDiff = now.diff(lastTimestamp, 'hours');
          if (hourDiff < 1) {
            const minuteDiff = now.diff(lastTimestamp, 'minutes');
            if (minuteDiff <= 1) {
              return `Updated < 1 minute ago`;
            }
            return `Updated ${minuteDiff} minutes ago`;
          }
          return `Updated ${hourDiff} hour${hourDiff > 1 ? 's' : ''} ago`;
        } else if (dayDiff >= 9) {
          const monthDiff = now.diff(lastTimestamp, 'months');
          if (monthDiff >= 6) {
            return `Last updated ${lastTimestamp.format('M-D-YYYY')}`;
          }
          return `Last updated ${lastTimestamp.format('MMM Do')}`;
        }
        return `Updated ${now.diff(lastTimestamp, 'days')} days ago`;
      }
    }

    return 'Update History';
  }, [data]);

  const expiration = useQuoteExpiration(data ? data : {});
  const { product, quoteNumber } = data;

  return (
    <LayoutBox width="100%">
      <NavBar padX={({ theme }) => theme.breakpoints({ sm: '$0', md: 0 })} padTop="$1">
        <AppBarSpacer />
        <NavRow xsHeight={48} padX="$5" borderBottomWidth={1} borderBottomColor="#DFE3E8" width={'100%'}>
          <NavGroup start>
            <NavBackButton
              path="/quotes"
              color="$gray.300"
              label={
                <Text>
                  Quotes /<Text color="$gray.700">{` # ${data?.quoteNumber}`}</Text>
                </Text>
              }
            />
          </NavGroup>
          <NavGroup end>
            <Text xSmall bold uppercase color="$gray.400">
              {expiration.text}
            </Text>
          </NavGroup>
        </NavRow>
      </NavBar>
      {data ? (
        <LayoutBox padding="$3" xsHeight={64} bg="#F9FAFB" width="100%">
          <LayoutBox row width="100%">
            <Box width="50%">
              <QuoteNav data={data} expiration={expiration.text} historyDisplayText={historyDisplayText} />
            </Box>
            <Box width="50%">
              <QuoteActions quote={data} refreshQuoteData={update} printRef={printRef} meow />
            </Box>
          </LayoutBox>
          <LayoutBox
            row
            style={{ color: '#919EAB', fontSize: 14, lineHeight: 22, fontWeight: 700, display: 'flex', rowGap: 6, columnGap: 24, flexWrap: 'wrap' }}
          >
            <span style={{ color: '#212B36 ' }}>Effective {moment.utc(data.coverageAt).format('MMM D, YYYY')}</span>

            <li>{expiration.text}</li>

            <li>Quote No. {quoteNumber}</li>

            <li>{historyDisplayText}</li>

            <li>{product.name}</li>
          </LayoutBox>
        </LayoutBox>
      ) : null}
    </LayoutBox>
  );
});

const RenderExitPromptIfEditing = memo(function RenderExitPromptIfEditing() {
  const { adjusting, disable } = useAdjustmentState();

  useEffect(() => {
    return () => {
      if (adjusting) {
        disable();
      }
    };
  }, [adjusting, disable]);

  return <Prompt when={adjusting} message="If you leave all your edits will be lost" />;
});
